import { createSlice, createAsyncThunk, createAction } from '@reduxjs/toolkit';
import {
  NuvemEnvioCreateDispatchesRequestDto,
  NuvemEnvioCreateDispatchesResponseDto,
  NuvemEnvioSimulateDispatchChargeRequestDto,
  NuvemEnvioSimulateDispatchChargeResponseDto,
  NuvemEnvioCreateCreditRechargeResponseDto,
  NuvemEnvioCreateCreditRechargeRequestDto,
  NuvemEnvioSearchLabelsResponseDto,
  NuvemEnvioDownloadLabelsResponseDto,
  NuvemEnvioSearchLabelsRequestDto,
  NuvemEnvioDownloadLabelsRequestDto,
  NuvemEnvioCreateLabelsResponseDto,
  NuvemEnvioCreateLabelsRequestDto,
} from '@tiendanube/common';
import { StatusType } from './types';
import ordersService from '../ordersService/ordersService';

const PROGRESS_COMPLETE = 100;

export type statusType = StatusType;

export interface InterfaceCreateNuvemEnvioLabelsSchema {
  data: NuvemEnvioCreateDispatchesResponseDto;
  status: statusType;
}

export interface InterfaceSimulateNuvemEnvioLabelsSchema {
  data: NuvemEnvioSimulateDispatchChargeResponseDto;
  status: statusType;
}

export interface InterfaceCreditRechargeNuvemEnvioLabelsSchema {
  data: NuvemEnvioCreateCreditRechargeResponseDto;
  status: statusType;
}
export interface InterfaceCreateNuvemEnvioAsyncLabelsSchema {
  data: NuvemEnvioCreateLabelsResponseDto;
  status: statusType;
}
export interface InterfaceSearchForNuvemEnvioLabelsSchema {
  data: NuvemEnvioSearchLabelsResponseDto;
  status: statusType;
}
export interface InterfaceDownloadNuvemEnvioLabelSchema {
  data: NuvemEnvioDownloadLabelsResponseDto;
  status: statusType;
}
export interface InterfaceNuvemEnvioOrdersSchema {
  createNuvemEnvioLabels: InterfaceCreateNuvemEnvioLabelsSchema;
  simulateNuvemEnvioLabels: InterfaceSimulateNuvemEnvioLabelsSchema;
  creditRechargeNuvemEnvioLabels: InterfaceCreditRechargeNuvemEnvioLabelsSchema;
  showNuvemEnvioPrintLabelModal: boolean;
  createNuvemEnvioAsyncLabels: InterfaceCreateNuvemEnvioAsyncLabelsSchema;
  searchForNuvemEnvioLabels: InterfaceSearchForNuvemEnvioLabelsSchema;
  downloadNuvemEnvioLabel: InterfaceDownloadNuvemEnvioLabelSchema;
}

const DEFAULT_STATUS = StatusType.Idle;

const initialState: InterfaceNuvemEnvioOrdersSchema = {
  createNuvemEnvioLabels: {
    data: {
      contentDeclarationUrls: [],
      labelUrls: [],
      errors: [],
    },
    status: DEFAULT_STATUS,
  },
  simulateNuvemEnvioLabels: {
    data: {
      balanceInfo: {
        currentBalanceAmount: 0,
        chargeableAmount: 0,
        suggestedRechargeAmount: 0,
        finalBalanceAmount: 0,
        shouldRecharge: false,
        chargeableOrders: [],
      },
      errors: [],
    },
    status: DEFAULT_STATUS,
  },
  creditRechargeNuvemEnvioLabels: {
    data: {
      checkoutUrl: '',
    },
    status: DEFAULT_STATUS,
  },
  showNuvemEnvioPrintLabelModal: false,
  createNuvemEnvioAsyncLabels: {
    data: {
      labels: [],
      errors: [],
    },
    status: DEFAULT_STATUS,
  },
  searchForNuvemEnvioLabels: {
    data: {
      readyLabels: [],
      pendingLabels: [],
      erroredLabels: [],
      totalCount: 0,
      finishedCount: 0,
      progressInPercentage: 0,
    },
    status: DEFAULT_STATUS,
  },
  downloadNuvemEnvioLabel: {
    data: {
      documentUrl: '',
    },
    status: DEFAULT_STATUS,
  },
};

export const createNuvemEnvioLabels = createAsyncThunk(
  'nuvemEnvioOrders/createNuvemEnvioLabels',
  async (params: NuvemEnvioCreateDispatchesRequestDto) =>
    ordersService.createNuvemEnvioLabels(params),
);

export const resetNuvemEnvioLabels = createAction(
  'nuvemEnvioOrders/resetNuvemEnvioLabels',
);

export const simulateNuvemEnvioLabels = createAsyncThunk(
  'nuvemEnvioOrders/simulateNuvemEnvioLabels',
  async (params: NuvemEnvioSimulateDispatchChargeRequestDto) =>
    ordersService.simulateNuvemEnvioLabels(params),
);

export const resetSimulateNuvemEnvioLabels = createAction(
  'nuvemEnvioOrders/resetSimulateNuvemEnvioLabels',
);

export const creditRechargeNuvemEnvioLabels = createAsyncThunk(
  'nuvemEnvioOrders/creditRechargeNuvemEnvioLabels',
  async (params: NuvemEnvioCreateCreditRechargeRequestDto) =>
    ordersService.creditRechargeNuvemEnvioLabels(params),
);

export const resetCreditRechargeNuvemEnvioLabels = createAction(
  'nuvemEnvioOrders/resetCreditRechargeNuvemEnvioLabels',
);

export const openNuvemEnvioPrintLabelModal = createAction(
  'nuvemEnvioOrders/openNuvemEnvioPrintLabelModal',
);

export const closeNuvemEnvioPrintLabelModal = createAction(
  'nuvemEnvioOrders/closeNuvemEnvioPrintLabelModal',
);

export const createNuvemEnvioAsyncLabels = createAsyncThunk(
  'nuvemEnvioOrders/createNuvemEnvioAsyncLabels',
  async (params: NuvemEnvioCreateLabelsRequestDto) =>
    ordersService.createNuvemEnvioAsyncLabels(params),
);

export const searchForNuvemEnvioLabels = createAsyncThunk(
  'nuvemEnvioOrders/searchForNuvemEnvioLabels',
  async (params: NuvemEnvioSearchLabelsRequestDto) =>
    ordersService.searchForNuvemEnvioLabels(params),
);

export const downloadNuvemEnvioLabel = createAsyncThunk(
  'nuvemEnvioOrders/downloadNuvemEnvioLabel',
  async (params: NuvemEnvioDownloadLabelsRequestDto) =>
    ordersService.downloadNuvemEnvioLabels(params),
);

const nuvemEnvioOrdersSlice = createSlice({
  name: 'printManager',
  initialState,
  reducers: {
    resetDownloadNuvemEnvioLabel(state) {
      state.downloadNuvemEnvioLabel = initialState.downloadNuvemEnvioLabel;
    },
    resetCreateNuvemEnvioAsyncLabels(state) {
      state.createNuvemEnvioAsyncLabels =
        initialState.createNuvemEnvioAsyncLabels;
    },
    resetSearchNuvemEnvioLabels(state) {
      state.searchForNuvemEnvioLabels = initialState.searchForNuvemEnvioLabels;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createNuvemEnvioLabels.fulfilled, (state, action) => {
        state.createNuvemEnvioLabels.status = StatusType.Success;
        state.createNuvemEnvioLabels.data = action.payload;
        return state;
      })
      .addCase(createNuvemEnvioLabels.pending, (state) => {
        state.createNuvemEnvioLabels.status = StatusType.Loading;
        return state;
      })
      .addCase(createNuvemEnvioLabels.rejected, (state) => {
        state.createNuvemEnvioLabels.status = StatusType.Error;
        return state;
      })
      .addCase(resetNuvemEnvioLabels, (state) => {
        state.createNuvemEnvioLabels = {
          data: {
            contentDeclarationUrls: [],
            labelUrls: [],
            errors: [],
          },
          status: DEFAULT_STATUS,
        };
      });
    builder
      .addCase(simulateNuvemEnvioLabels.fulfilled, (state, action) => {
        state.simulateNuvemEnvioLabels.status = StatusType.Success;
        state.simulateNuvemEnvioLabels.data = action.payload;
        return state;
      })
      .addCase(simulateNuvemEnvioLabels.pending, (state) => {
        state.simulateNuvemEnvioLabels.status = StatusType.Loading;
        return state;
      })
      .addCase(simulateNuvemEnvioLabels.rejected, (state) => {
        state.simulateNuvemEnvioLabels.status = StatusType.Error;
        return state;
      })
      .addCase(resetSimulateNuvemEnvioLabels, (state) => {
        state.simulateNuvemEnvioLabels = {
          data: {
            balanceInfo: {
              currentBalanceAmount: 0,
              chargeableAmount: 0,
              suggestedRechargeAmount: 0,
              finalBalanceAmount: 0,
              shouldRecharge: false,
              chargeableOrders: [],
            },
            errors: [],
          },
          status: DEFAULT_STATUS,
        };
      });
    builder
      .addCase(creditRechargeNuvemEnvioLabels.fulfilled, (state, action) => {
        state.creditRechargeNuvemEnvioLabels.status = StatusType.Success;
        state.creditRechargeNuvemEnvioLabels.data = action.payload;
        return state;
      })
      .addCase(creditRechargeNuvemEnvioLabels.pending, (state) => {
        state.creditRechargeNuvemEnvioLabels.status = StatusType.Loading;
        return state;
      })
      .addCase(creditRechargeNuvemEnvioLabels.rejected, (state) => {
        state.creditRechargeNuvemEnvioLabels.status = StatusType.Error;
        return state;
      })
      .addCase(resetCreditRechargeNuvemEnvioLabels, (state) => {
        state.creditRechargeNuvemEnvioLabels = {
          data: {
            checkoutUrl: '',
          },
          status: DEFAULT_STATUS,
        };
      });
    builder
      .addCase(openNuvemEnvioPrintLabelModal, (state) => {
        state.showNuvemEnvioPrintLabelModal = true;
      })
      .addCase(closeNuvemEnvioPrintLabelModal, (state) => {
        state.showNuvemEnvioPrintLabelModal = false;
      });
    builder
      .addCase(createNuvemEnvioAsyncLabels.fulfilled, (state, action) => {
        state.createNuvemEnvioAsyncLabels.status = StatusType.Success;
        state.createNuvemEnvioAsyncLabels.data = action.payload;
        return state;
      })
      .addCase(createNuvemEnvioAsyncLabels.pending, (state) => {
        state.createNuvemEnvioAsyncLabels.status = StatusType.Loading;
        return state;
      })
      .addCase(createNuvemEnvioAsyncLabels.rejected, (state) => {
        state.createNuvemEnvioAsyncLabels.status = StatusType.Error;
        return state;
      });
    builder
      .addCase(searchForNuvemEnvioLabels.fulfilled, (state, action) => {
        const progressInPercentage = action.payload.progressInPercentage;
        if (progressInPercentage === PROGRESS_COMPLETE) {
          state.searchForNuvemEnvioLabels.status = StatusType.Success;
          state.searchForNuvemEnvioLabels.data = action.payload;
        } else {
          state.searchForNuvemEnvioLabels.status = StatusType.Loading;
          state.searchForNuvemEnvioLabels.data = action.payload;
        }
        return state;
      })
      .addCase(searchForNuvemEnvioLabels.pending, (state) => {
        state.searchForNuvemEnvioLabels.status = StatusType.Loading;
        return state;
      })
      .addCase(searchForNuvemEnvioLabels.rejected, (state) => {
        state.searchForNuvemEnvioLabels.status = StatusType.Error;
        return state;
      });
    builder
      .addCase(downloadNuvemEnvioLabel.fulfilled, (state, action) => {
        state.downloadNuvemEnvioLabel.status = StatusType.Success;
        state.downloadNuvemEnvioLabel.data = action.payload;
        return state;
      })
      .addCase(downloadNuvemEnvioLabel.pending, (state) => {
        state.downloadNuvemEnvioLabel.status = StatusType.Loading;
        return state;
      })
      .addCase(downloadNuvemEnvioLabel.rejected, (state) => {
        state.downloadNuvemEnvioLabel.status = StatusType.Error;
        return state;
      });
  },
});

export const {
  resetDownloadNuvemEnvioLabel,
  resetCreateNuvemEnvioAsyncLabels,
  resetSearchNuvemEnvioLabels,
} = nuvemEnvioOrdersSlice.actions;

export const { reducer } = nuvemEnvioOrdersSlice;
